/**
 * Validation rules implemented identically to the backend (sk.ssi.evidencia.common.validation.*Rules).
 * Both test suites use the same vectors (rules.test.ts ↔ ValidationRulesTest.java).
 */
import { getCountries, getCountryCallingCode, Metadata, parsePhoneNumberFromString } from 'libphonenumber-js/max';

/** IČO: 8 digits, weights 8..2, check digit (11 − s mod 11) mod 10. Spaces are ignored. */
export function normalizeIco(input: string): string {
  return input.replace(/\s+/g, '');
}

export function isValidIco(input: string | null | undefined): boolean {
  if (input == null) return false;
  const ico = normalizeIco(input);
  if (!/^\d{8}$/.test(ico)) return false;
  let sum = 0;
  for (let i = 0; i < 7; i++) sum += Number(ico[i]) * (8 - i);
  return (11 - (sum % 11)) % 10 === Number(ico[7]);
}

/** EDUID: 9 digits, the identifier of a school or school facility in the Register of schools. Spaces are ignored. */
export function normalizeEduid(input: string): string {
  return input.replace(/\s+/g, '');
}

export function isValidEduid(input: string | null | undefined): boolean {
  return input != null && /^\d{9}$/.test(normalizeEduid(input));
}

/** Region number of the CVTI SR code lists = the first digit of the codes of its districts (Region.java). */
const REGION_NUMBERS: Record<string, string> = {
  BRATISLAVSKY: '1',
  TRNAVSKY: '2',
  TRENCIANSKY: '3',
  NITRIANSKY: '4',
  ZILINSKY: '5',
  BANSKOBYSTRICKY: '6',
  PRESOVSKY: '7',
  KOSICKY: '8',
};

/** The district (optional) lies in the region (DistrictInRegion.java); true while either is missing. */
export function isDistrictInRegion(district: string | null | undefined, region: string | null | undefined): boolean {
  if (!district || !region) return true;
  return REGION_NUMBERS[region] === district.charAt(0);
}

/** PSČ: ^[089]\d{2} ?\d{2}$, stored as 5 digits. */
export function isValidPsc(input: string | null | undefined): boolean {
  return input != null && /^[089]\d{2} ?\d{2}$/.test(input.trim());
}

export function normalizePsc(input: string): string {
  return input.replace(/ /g, '').trim();
}

export function isValidHouseNumber(input: string | null | undefined): boolean {
  return input != null && input.length <= 16 && /^\d{1,6}[A-Za-z]?(\/\d{1,6}[A-Za-z]?)?$/.test(input);
}

/** Removes ASCII whitespace and the non-breaking spaces – the same characters as Text.withoutSpaces (Java). */
function withoutSpaces(input: string): string {
  return input.replace(/[ \t\n\v\f\r\u00A0\u2007\u202F]+/g, '');
}

const RC_FORMAT = /^(\d{6})([/-]?)(\d{3,4})$/;

/** Today in Europe/Bratislava as a UTC-midnight timestamp. */
function todayInBratislava(): Date {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Bratislava', year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date())
    .split('-')
    .map(Number);
  return new Date(Date.UTC(parts[0]!, parts[1]! - 1, parts[2]!));
}

/**
 * Rodné číslo (see RodneCisloRules.java): YYMMDD/XXXX or YYMMDDXXXX, or YYMMDD/XXX before 1954 (no checksum); spaces
 * are ignored and the separator may be "/" or "-"; months 01–12/51–62 and 21–32/71–82; real, non-future date;
 * mod 11 checksum with the legacy exception.
 */
export function isValidRodneCislo(input: string | null | undefined, today: Date = todayInBratislava()): boolean {
  if (input == null) return false;
  const m = RC_FORMAT.exec(withoutSpaces(input));
  if (!m) return false;
  const datePart = m[1]!;
  const serial = m[3]!;
  const nineDigits = serial.length === 3;
  if (nineDigits && !m[2]) return false;
  const yy = Number(datePart.slice(0, 2));
  const mm = Number(datePart.slice(2, 4));
  const dd = Number(datePart.slice(4, 6));
  let year: number;
  if (nineDigits) {
    if (yy > 53) return false;
    year = 1900 + yy;
  } else {
    year = yy >= 54 ? 1900 + yy : 2000 + yy;
  }
  let month: number;
  if (mm >= 1 && mm <= 12) month = mm;
  else if (mm >= 51 && mm <= 62) month = mm - 50;
  else if (mm >= 21 && mm <= 32) month = mm - 20;
  else if (mm >= 71 && mm <= 82) month = mm - 70;
  else return false;
  const birth = new Date(Date.UTC(year, month - 1, dd));
  if (birth.getUTCFullYear() !== year || birth.getUTCMonth() !== month - 1 || birth.getUTCDate() !== dd) return false;
  const todayUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  if (birth.getTime() > todayUtc) return false;
  if (nineDigits) return true;
  const number = BigInt(datePart + serial);
  if (number % 11n === 0n) return true;
  return (number / 10n) % 11n === 10n && number % 10n === 0n;
}

export function normalizeRodneCislo(input: string): string {
  const m = RC_FORMAT.exec(withoutSpaces(input));
  return m ? `${m[1]}/${m[3]}` : input.trim();
}

// Masked form for non-admin users: six asterisks, slash, the last digits.
export function maskRodneCislo(input: string | null | undefined): string {
  if (!input) return '';
  const digits = input.replace(/\D/g, '');
  return digits.length < 9 ? '******' : `******/${digits.slice(6)}`;
}

const PHONE_FORMAT = /^\+[0-9]{4,15}$/;

function parsePhone(input: string | null | undefined) {
  if (input == null) return undefined;
  const compact = withoutSpaces(input);
  if (!PHONE_FORMAT.test(compact)) return undefined;
  return parsePhoneNumberFromString(compact);
}

/** International format starting with "+", validated with libphonenumber (max metadata). */
export function isValidPhone(input: string | null | undefined): boolean {
  return parsePhone(input)?.isValid() ?? false;
}

/** Why a phone number is not valid: a message key (`validation.phone…`) and its parameters. */
export interface PhoneProblem {
  key: 'validation.phone' | 'validation.phoneCountry' | 'validation.phonePrefix' | 'validation.phoneTooShort' | 'validation.phoneTooLong' | 'validation.phoneInvalid';
  params?: { code: string; prefix?: string };
}

/**
 * The problem of a phone number, undefined when it is valid (see PhoneRules.java, same rules):
 * - not "+" and 4–15 digits → `validation.phone` (international format);
 * - no country calling code → `validation.phoneCountry`, `code` = the shortest beginning no calling code starts with;
 * - after the calling code, digits no number of that country starts with → `validation.phonePrefix`, `prefix` = the
 *   national digits up to the first one that does not fit ("+421 39…" → 39);
 * - every digit fits but the number is not complete → `validation.phoneTooShort`; a complete number followed by more
 *   digits → `validation.phoneTooLong`; otherwise `validation.phoneInvalid`.
 */
export function phoneProblem(input: string | null | undefined): PhoneProblem | undefined {
  const compact = input == null ? '' : withoutSpaces(input);
  if (!PHONE_FORMAT.test(compact)) return { key: 'validation.phone' };
  const number = parsePhoneNumberFromString(compact);
  if (number?.isValid()) return undefined;
  const digits = compact.slice(1);
  const code = callingCodes().find((c) => digits.startsWith(c));
  if (!code) {
    let n = 1;
    while (callingCodes().some((c) => c.startsWith(digits.slice(0, n)))) n++;
    return { key: 'validation.phoneCountry', params: { code: digits.slice(0, n) } };
  }
  const national = number?.nationalNumber ?? digits.slice(code.length);
  const patterns = numberPatterns(code);
  const fits = (s: string) => patterns.some((p) => p.prefix.test(s));
  const complete = (s: string) => patterns.some((p) => p.whole.test(s));
  let fitting = 0;
  while (fitting < national.length && fits(national.slice(0, fitting + 1))) fitting++;
  if (fitting === national.length) return { key: complete(national) ? 'validation.phoneInvalid' : 'validation.phoneTooShort', params: { code } };
  if (complete(national.slice(0, fitting))) return { key: 'validation.phoneTooLong', params: { code } };
  return { key: 'validation.phonePrefix', params: { code, prefix: national.slice(0, fitting + 1) } };
}

/** The runtime metadata API of libphonenumber-js that its typings leave out (the number patterns by type). */
interface PhoneMetadata {
  getCountryCodesForCallingCode(callingCode: string): string[] | undefined;
  nonGeographic(): Record<string, unknown> | undefined;
  selectNumberingPlan(countryOrCallingCode: string): { numberingPlan: { type(type: string): { pattern(): string } | undefined } };
}

const PHONE_TYPES = ['FIXED_LINE', 'MOBILE', 'TOLL_FREE', 'PREMIUM_RATE', 'PERSONAL_NUMBER', 'VOICEMAIL', 'UAN', 'PAGER', 'VOIP', 'SHARED_COST'];
let phoneMetadata: PhoneMetadata | undefined;
let allCallingCodes: string[] | undefined;
const patternsByCode = new Map<string, { whole: RegExp; prefix: RegExp }[]>();

function metadata(): PhoneMetadata {
  return (phoneMetadata ??= new Metadata() as unknown as PhoneMetadata);
}

/** All country calling codes (geographic and non-geographic, e.g. 800). They are prefix-free (ITU-T E.164). */
function callingCodes(): string[] {
  return (allCallingCodes ??= [...new Set([...getCountries().map((c) => getCountryCallingCode(c)), ...Object.keys(metadata().nonGeographic() ?? {})])]);
}

/** The national number patterns of every type of every country with the calling code (+1: US, CA, …). */
function numberPatterns(code: string): { whole: RegExp; prefix: RegExp }[] {
  let patterns = patternsByCode.get(code);
  if (!patterns) {
    const plans = metadata().getCountryCodesForCallingCode(code) ?? [code];
    patterns = plans.flatMap((plan) => {
      const numbering = metadata().selectNumberingPlan(plan).numberingPlan;
      return PHONE_TYPES.map((type) => numbering.type(type)?.pattern()).filter((p): p is string => !!p);
    }).map((p) => ({ whole: new RegExp(`^(?:${p})$`), prefix: new RegExp(`^(?:${prefixPattern(p)})$`) }));
    patternsByCode.set(code, patterns);
  }
  return patterns;
}

/**
 * A pattern matching every beginning (including the empty one and the whole) of what `pattern` matches. The number
 * patterns of libphonenumber only use digits, \d, [...], (?:...), |, ? and {n} / {n,m}; anything else throws.
 * Exported for tests; PhoneRules.prefixPattern (Java) builds the same pattern.
 */
export function prefixPattern(pattern: string): string {
  let i = 0;
  type Part = { whole: string; prefix: string };
  const alternatives = (): Part => {
    const options = [sequence()];
    while (pattern[i] === '|') {
      i++;
      options.push(sequence());
    }
    return { whole: options.map((o) => o.whole).join('|'), prefix: options.map((o) => o.prefix).join('|') };
  };
  // the beginnings of "a b c": a beginning of a, or the whole a followed by a beginning of "b c"
  const sequence = (): Part => {
    const parts: Part[] = [];
    while (i < pattern.length && pattern[i] !== '|' && pattern[i] !== ')') parts.push(quantified(atom()));
    let prefix = '';
    for (let k = parts.length - 1; k >= 0; k--) prefix = k === parts.length - 1 ? parts[k]!.prefix : `(?:${parts[k]!.prefix}|${parts[k]!.whole}${prefix})`;
    return { whole: parts.map((p) => p.whole).join(''), prefix };
  };
  const atom = (): Part => {
    if (pattern.startsWith('(?:', i)) {
      i += 3;
      const inner = alternatives();
      if (pattern[i++] !== ')') throw new Error(`Unsupported phone number pattern: ${pattern}`);
      return { whole: `(?:${inner.whole})`, prefix: `(?:${inner.prefix})` };
    }
    let whole: string;
    if (pattern[i] === '[') {
      const end = pattern.indexOf(']', i);
      whole = pattern.slice(i, end + 1);
      i = end + 1;
    } else if (pattern.startsWith('\\d', i)) {
      whole = '\\d';
      i += 2;
    } else if (/[0-9]/.test(pattern[i] ?? '')) {
      whole = pattern[i++]!;
    } else {
      throw new Error(`Unsupported phone number pattern: ${pattern}`);
    }
    return { whole, prefix: `${whole}?` };
  };
  // the beginnings of a{min,max}: up to max − 1 whole repetitions followed by a beginning of one more
  const quantified = (a: Part): Part => {
    let min = 1;
    let max = 1;
    if (pattern[i] === '?') {
      i++;
      min = 0;
    } else if (pattern[i] === '{') {
      const end = pattern.indexOf('}', i);
      const [lo, hi] = pattern.slice(i + 1, end).split(',');
      min = Number(lo);
      max = hi === undefined ? min : Number(hi);
      i = end + 1;
    }
    if (min === 1 && max === 1) return a;
    return { whole: `(?:${a.whole}){${min},${max}}`, prefix: max === 1 ? a.prefix : `(?:${a.whole}){0,${max - 1}}(?:${a.prefix})` };
  };
  const result = alternatives();
  if (i !== pattern.length) throw new Error(`Unsupported phone number pattern: ${pattern}`);
  return result.prefix;
}

export function toE164(input: string): string {
  return parsePhone(input)?.number ?? input;
}

/** "+421905123456" → "+421 905 123 456" */
export function formatPhone(e164: string | null | undefined): string {
  if (!e164) return '';
  return parsePhone(e164)?.formatInternational() ?? e164;
}

/** Same grammar as EmailRules.java. */
export const EMAIL_PATTERN =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

export function isValidEmail(input: string | null | undefined): boolean {
  return input != null && input.length <= 254 && EMAIL_PATTERN.test(input);
}

/** Letters (incl. diacritics), space, hyphen, apostrophe; starts with a letter. */
export function isValidPersonName(input: string | null | undefined): boolean {
  return input != null && /^\p{L}[\p{L}\p{M} '’-]*$/u.test(input);
}
